import { supabase } from '../lib/supabaseClient';
import type {
  NewsArticle,
  NewsComment,
  CreateNewsArticleInput,
  UpdateNewsArticleInput,
} from '../types/newsArticle';

const NEWS_BUCKET = 'news-images';
const DEFAULT_PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function fetchNewsArticles(
  offset = 0,
  limit = DEFAULT_PAGE_SIZE
): Promise<{ data: NewsArticle[]; error: Error | null; hasMore: boolean }> {
  const { data, error } = await supabase
    .from('news')
    .select('id, title, content, image_url, author, created_at, updated_at')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    return { data: [], error, hasMore: false };
  }

  const rows = (data ?? []) as NewsArticle[];
  return { data: rows, error: null, hasMore: rows.length === limit };
}

export async function fetchNewsArticleById(
  id: string
): Promise<{ data: NewsArticle | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('news')
    .select('id, title, content, image_url, author, created_at, updated_at')
    .eq('id', id)
    .single();

  if (error) {
    return { data: null, error };
  }

  return { data: data as NewsArticle, error: null };
}

// ---------------------------------------------------------------------------
// Write (admin only — enforced by Supabase RLS)
// ---------------------------------------------------------------------------

export async function createNewsArticle(
  input: CreateNewsArticleInput
): Promise<{ data: NewsArticle | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('news')
    .insert({
      title: input.title,
      content: input.content,
      image_url: input.image_url ?? null,
      author: input.author ?? null,
    })
    .select()
    .single();

  if (error) return { data: null, error };
  return { data: data as NewsArticle, error: null };
}

export async function updateNewsArticle(
  id: string,
  input: UpdateNewsArticleInput
): Promise<{ data: NewsArticle | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('news')
    .update({
      title: input.title,
      content: input.content,
      image_url: input.image_url,
      author: input.author,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) return { data: null, error };
  return { data: data as NewsArticle, error: null };
}

export async function deleteNewsArticle(id: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.from('news').delete().eq('id', id);
  return { error };
}

// ---------------------------------------------------------------------------
// Image upload (stored in news-images bucket)
// ---------------------------------------------------------------------------

export async function uploadNewsArticleImage(
  file: File
): Promise<{ url: string | null; error: string | null }> {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from(NEWS_BUCKET)
    .upload(fileName, file, { cacheControl: '3600', upsert: false });

  if (uploadError) {
    return { url: null, error: uploadError.message };
  }

  const { data: urlData } = supabase.storage.from(NEWS_BUCKET).getPublicUrl(fileName);
  return { url: urlData.publicUrl, error: null };
}

// ---------------------------------------------------------------------------
// News comments (news_comments table)
// ---------------------------------------------------------------------------

async function fetchCommentAuthorNames(authorIds: string[]): Promise<Map<string, string>> {
  if (authorIds.length === 0) return new Map();
  const { data } = await supabase
    .from('profiles')
    .select('user_id, username')
    .in('user_id', authorIds);
  const map = new Map<string, string>();
  for (const row of data ?? []) map.set(row.user_id as string, row.username as string);
  return map;
}

export async function fetchNewsComments(
  articleId: string,
  limit?: number
): Promise<{ data: NewsComment[]; total: number; error: Error | null }> {
  const { count } = await supabase
    .from('news_comments')
    .select('id', { count: 'exact', head: true })
    .eq('article_id', articleId);

  let query = supabase
    .from('news_comments')
    .select('id, article_id, author_id, content, created_at')
    .eq('article_id', articleId)
    .order('created_at', { ascending: true });

  if (limit !== undefined) query = query.limit(limit);

  const { data, error } = await query;
  if (error) return { data: [], total: 0, error };

  const rows = (data ?? []) as Record<string, unknown>[];
  const authorIds = Array.from(new Set(rows.map((r) => r.author_id as string)));
  const nameMap = await fetchCommentAuthorNames(authorIds);

  return {
    data: rows.map((r) => ({
      id: r.id as string,
      article_id: r.article_id as string,
      author_id: r.author_id as string,
      author_display_name: nameMap.get(r.author_id as string),
      content: r.content as string,
      created_at: r.created_at as string,
    })),
    total: count ?? 0,
    error: null,
  };
}

export async function addNewsComment(
  articleId: string,
  authorId: string,
  content: string
): Promise<{ data: NewsComment | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('news_comments')
    .insert({ article_id: articleId, author_id: authorId, content })
    .select('id, article_id, author_id, content, created_at')
    .single();

  if (error) return { data: null, error };

  const row = data as Record<string, unknown>;
  const nameMap = await fetchCommentAuthorNames([row.author_id as string]);

  return {
    data: {
      id: row.id as string,
      article_id: row.article_id as string,
      author_id: row.author_id as string,
      author_display_name: nameMap.get(row.author_id as string),
      content: row.content as string,
      created_at: row.created_at as string,
    },
    error: null,
  };
}

export async function deleteNewsComment(commentId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.from('news_comments').delete().eq('id', commentId);
  return { error };
}
